import type { UserPsi } from "@phurshell/psiapp-frontend-core/domain";
import { languages as languageFlags } from "@/utils/languages";
import { userAvatars } from "@/utils/userAvatars";

/**
 * Psi no formato que as telas do main usam, a partir do `UserPsi` do core.
 *
 * O `UserMapperImpl.toDomain` do core já calcula nota, experiência, abordagens,
 * vídeo e premium com as mesmas regras do `PsiMappers` legado. Aqui só entra o que
 * é de apresentação do paciente (web) e o core não faz igual:
 * - foto como string, com os fallbacks locais (avatar padrão / usuário removido);
 * - `canHaveFirstAppointment`;
 * - bandeira em `languages[].detail.flag`;
 * - avatar local de quem avaliou (o core deixa só a chave em `img`).
 * Equivalência com o legado: tests/unit/formatters/psi.spec.ts (respostas reais da API).
 */
export type PsiView = Omit<UserPsi, "formatted_profile_image"> & {
  formatted_profile_image: string;
  canHaveFirstAppointment: boolean;
};

export function profileImageUrl(psi: Pick<UserPsi, "profile_image" | "deleted_at">): string {
  if (psi.deleted_at) return require("@/assets/img/unavailable_user_square.png");
  if (psi.profile_image) return `${process.env.VUE_APP_BASE_IMG_URL}${psi.profile_image}`;
  return require("@/assets/img/avatars/1.png");
}

export function toPsiView(psi: UserPsi): PsiView {
  return {
    ...psi,
    formatted_profile_image: profileImageUrl(psi),
    canHaveFirstAppointment: !!psi.first_appointment && psi.appointments_with_this_psy_count == 0,
    languages: (psi.languages ?? []).map((l: any) => ({
      ...l,
      detail: { ...l.detail, flag: (languageFlags as any)[l.detail?.code]?.flag },
    })),
    formatted_latest_ratings: (psi.formatted_latest_ratings ?? []).map((r: any) => ({
      ...r,
      img: userAvatars[r.user?.profile_image || 1],
    })),
  } as PsiView;
}

/** Avaliação de um psi com o avatar local e as iniciais de quem avaliou (como o legado). */
export function toRatingView(rating: any) {
  return {
    ...rating,
    img: userAvatars[rating.user?.profile_image || 1],
    initials: `${rating.user?.name?.charAt(0) ?? ""}.`,
  };
}

/** Nome de quem avaliou no card do depoimento: o primeiro nome, como no app e no pro. */
export function ratingAuthorName(rating: any): string {
  return rating?.user?.name?.trim().split(" ")[0] || rating?.initials || "";
}
